import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as HieroglyphData from "../Data/HieroglyphData";
import { ColourRampFilter } from "../Filters/ColourRampFilter";

export class HieroglyphLayer extends PIXI.Container {
    private _layerIndex: number;
    private _contentIndex: number;
    private _colourIndex: number;
    private _elementContainer: PIXI.Container;
    private _elements: PIXI.Sprite[];
    private _elementOutlines: PIXI.Sprite[];
    private _elementTemp!: PIXI.Sprite;
    private _colourRamp!: PIXI.Texture;
    private _filter!: ColourRampFilter;
    private _iconFilters: Array<PIXI.Filter<any>>;
    private _isHidden: boolean;

    constructor(pLayerIndex: number) {
        super();
        this._layerIndex = pLayerIndex;
        this._contentIndex = 0;
        this._colourIndex = -1;
        this._isHidden = false;

        if (this._layerIndex === 0) {
            this._elementTemp = new PIXI.Sprite();
            this._elementTemp.anchor.set(0.5);
            this.addChild(this._elementTemp);
            this._elementTemp.visible = false;
        }

        this._elementContainer = new PIXI.Container();
        this.addChild(this._elementContainer);

        this._elements = new Array<PIXI.Sprite>();
        this._elementOutlines = new Array<PIXI.Sprite>();

        this._iconFilters = [];

        this.setContent(0, true);
    }

    public get contentIndex(): number {
        return this._contentIndex;
    }

    public get colourIndex(): number {
        return this._colourIndex;
    }

    public get isHidden(): boolean {
        return this._isHidden;
    }

    public setContent(pIndex: number, pInstantly: boolean = false): void {
        this._contentIndex = pIndex;
        const timeline: TimelineLite = new TimelineLite();
        if (pInstantly === false && this._layerIndex === 0) {
            this._elementTemp.texture = this._elements[0].texture;
            this._elementTemp.visible = true;
        }

        timeline.to(this._elementContainer, pInstantly || this._layerIndex === 0 ? 0 : 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
        });
        timeline.call(() => { this.enableElements(); });
        timeline.to(this._elementContainer, pInstantly ? 0 : 0.25, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        if (pInstantly === false && this._layerIndex === 0) {
            timeline.call(() => {
                this._elementTemp.visible = false;
            });
        }
    }

    public setColourIndex(pIndex: number, pInstantly: boolean = false): void {
        this._colourIndex = pIndex;
        if (this._layerIndex === 2) {
            if (this._colourIndex === -1) {
                this._iconFilters = [];
            }
            else {
                const texture: string = "colour-ramp-0" + this._colourIndex;
                this._colourRamp = PIXI.loader.resources[texture].texture;
                if (this._filter === undefined) {
                    this._filter = new ColourRampFilter(this._colourRamp);
                }
                else {
                    this._filter.uniforms.uRamp = this._colourRamp;
                }
                this._iconFilters = [ this._filter ];
            }
            for (let i = 0; i < this._elements.length; ++i) {
                this._elements[i].filters = this._iconFilters;
            }
        }
    }

    public hide(pInstantly: boolean = false): void {
        this._isHidden = true;
        TweenLite.to(this, pInstantly ? 0 : 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
        });
    }

    public show(pInstantly: boolean = false): void {
        this._isHidden = false;
        TweenLite.to(this, pInstantly ? 0 : 0.75, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
    }

    private addElement(): void {
        const element: PIXI.Sprite = new PIXI.Sprite();
        element.anchor.set(0.5);
        this._elements.push(element);
        this._elementContainer.addChild(element);
        if (this._layerIndex === 2) {
            const outline: PIXI.Sprite = new PIXI.Sprite();
            outline.anchor.set(0.5);
            this._elementOutlines.push(outline);
            this._elementContainer.addChild(outline);
            element.filters = this._iconFilters;
        }
    }

    private enableElements(): void {
        const count: number = HieroglyphData.LAYER_DATA[this._layerIndex][this._contentIndex];
        // Add more elements if we don't have enough
        while (this._elements.length < count) {
            this.addElement();
        }
        // Enable visibility on necessary elements
        for (let i = 0; i < this._elements.length; ++i) {
            this._elements[i].visible = i < count;
            if (this._layerIndex === 2) {
                this._elementOutlines[i].visible = this._elements[i].visible;
            }
        }
        // Set to correct sprites
        for (let i = 0; i < count; ++i) {
            let sheet: string = Constants.SPRITESHEET_HIERO_PREFIX;
            switch (this._layerIndex) {
                case 0:
                    sheet += this._contentIndex < 2 ? "00" : "01";
                    break;
                case 1:
                    sheet += "02";
                    break;
                case 2:
                    sheet += "03";
                    break;
            }
            const asset: string =
                "layer" +
                HLF.NumberUtils.addZeroPadding(this._layerIndex, 2) + "-" +
                HLF.NumberUtils.addZeroPadding(this._contentIndex, 2) + "-" +
                HLF.NumberUtils.addZeroPadding(i, 2);
            this._elements[i].texture = PIXI.loader.resources[sheet].textures![asset];
            if (this._layerIndex === 2) {
                this._elementOutlines[i].texture = PIXI.loader.resources[sheet].textures![asset + "-outline"];
            }
        }
    }
}
